<template>
  <div>
    <div class="col-12 bg-secondary-1 mb-2">
      <div class="calendar-datarange-selector">
        <ul class="single-data mr-2 col-12">
          <li class="rounded bg-light-white cursor-pointer float-left action">
            <a @click="changeSelectedDay(-1)">
              <i class="fa fa-chevron-left" />
            </a>
          </li>
          <li class="rounded bg-light-white cursor-pointer float-right action">
            <a @click="changeSelectedDay(1)">
              <i class="fa fa-chevron-right" />
            </a>
          </li>
          <li class="rounded bg-secondary-1 font-16 font-weight-bold title ">
            <div class="date-range">
              {{ options[0] ? options[0].title : '' }}
            </div>
            <div v-click-outside="closeDatePicker" class="datepicker-container">
              <div class="date-range-icon" @click="openDatePicker">
                <i
                  :class="isOpen ? `fas fa-chevron-down` : `fas fa-chevron-up`"
                />
              </div>
              <div v-if="isOpen" class="date-picker">
                <CustomDatepickerVue2
                  :calendar-type="isDayOrWeek"
                  :max-date="disabledAfterMaxDate"
                  @calendar-input="setDatePickerValue"
                />
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import calendarViewModes from '@/utils/shared/constants/calendarViewModes';
import moment from 'moment';
import CustomDatepickerVue2 from '@/components/common/form/CustomDatepicker-vue2.vue';

export default {
  name: 'VisitCalendarDateRangeSelector',
  components: {
    CustomDatepickerVue2,
  },
  directives: {
    'click-outside': {
      bind: function(el, binding) {
        // Define Handler and cache it on the element
        const bubble = binding.modifiers.bubble;
        const handler = (e) => {
          if (bubble || (!el.contains(e.target) && el !== e.target)) {
            binding.value(e);
          }
        };
        el.__vueClickOutside__ = handler;

        // add Event Listeners
        document.addEventListener('click', handler);
      },

      unbind: function(el) {
        // Remove Event Listeners
        document.removeEventListener('click', el.__vueClickOutside__);
        el.__vueClickOutside__ = null;
      },
    },
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        month: 0,
        week: 0,
        day: 0,
        startDate: null,
        endDate: null,
      }),
      newMaxDate: {
        type: Date,
        default: () => null,
      },
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    year: {
      type: Number,
      default: () => new Date().getFullYear(),
    },
    academicYearInfo: {
      type: Object,
      default: () => null,
    },
    viewMode: {
      type: Number,
      default: () => calendarViewModes.Week,
    },
    showWeekRange: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      minDate: null,
      maxDate: null,
      options: [],
      isOpen: false,
      calendarInput: null,
    };
  },
  computed: {
    currentOptions() {
      return [
        { id: calendarViewModes.Month, name: 'Mes' },
        { id: calendarViewModes.Week, name: 'Semana' },
        { id: calendarViewModes.Day, name: 'Día' },
      ];
    },
    availableOptions() {
      return this.options;
    },
    isDayOrWeek() {
      return this.viewMode == calendarViewModes.Day ? 'date' : 'week';
    },
  },
  watch: {
    academicYearInfo(newValue) {
      if (!newValue) return;

      if (newValue.isCurrentYear) {
        this.setInitialOptions();
      } else {
        this.setInitialOptions(newValue.toDate);
      }
      this.updateOptions();
    },
    value(newValue) {
      let currentSelectedOption = { firstDate: newValue.startDate };

      if (this.viewMode == calendarViewModes.Month) {
        currentSelectedOption.id =
          currentSelectedOption.firstDate.getMonth() + 1;
      } else if (this.viewMode == calendarViewModes.Week) {
        //Change the start date based on the week number
        currentSelectedOption.firstDate = moment(
          currentSelectedOption.firstDate
        )
          .startOf('week')
          .toDate();

        currentSelectedOption.id = newValue.week;
      } else {
        currentSelectedOption.id = newValue.day;
      }

      this.optionSelected(currentSelectedOption);
    },
    viewMode() {
      let currentSelectedOption = {
        firstDate:
          this.value.startDate != null
            ? moment(this.value.startDate)
                .startOf('week')
                .toDate()
            : moment(new Date())
                .startOf('week')
                .toDate(),
      };

      if (this.viewMode == calendarViewModes.Week) {
        currentSelectedOption.id = this.value.week;
      } else {
        currentSelectedOption.id = this.value.day;
      }

      this.optionSelected(currentSelectedOption);
    },
  },
  mounted() {
    if (this.value.startDate == null) {
      this.setInitialOptions();
      this.updateOptions();
    }
  },
  methods: {
    setInitialOptions(academicYearToDate = null) {
      let date;
      // if not academic year last date, then use current date
      if (!academicYearToDate) {
        const currentDate = moment().toDate();
        date = currentDate;
      } else {
        date = moment(academicYearToDate).toDate();
      }

      this.value.month = date.getMonth() + 1;
      this.value.week = moment(date).week();
      this.value.day = date.getDate();

      this.maxDate = moment(date)
        .clone()
        .endOf('isoWeek');
      this.value.startDate = moment(date)
        .clone()
        .startOf('isoWeek')
        .toDate();
      this.value.endDate = moment(date)
        .clone()
        .endOf('isoWeek')
        .toDate();

      this.$emit('on-datarange-changed', this.value);
    },
    changeSelectedDay(day) {
      let startDate = null;
      let endDate = null;

      if (this.viewMode == calendarViewModes.Week) {
        day = day * 7;
      }

      startDate = moment(this.value.startDate)
        .add(day, 'day')
        .toDate();

      endDate = moment(this.value.endDate)
        .add(day, 'day')
        .toDate();

      if (endDate <= this.minDate || startDate >= this.maxDate) {
        return;
      }

      this.value.startDate = startDate;
      this.value.endDate = endDate;

      this.refreshSelection();
    },
    refreshSelection() {
      this.value.month = this.value.startDate.getMonth();
      this.value.week = moment(this.value.startDate).week();
      this.value.day = this.value.startDate.getDate();

      this.$emit('on-datarange-changed', this.value);

      this.updateOptions();
    },
    updateOptions() {
      this.options = [];

      if (this.viewMode == calendarViewModes.Month) {
        let currentDate = new Date(this.value.startDate.getTime());

        this.options.push({
          id: currentDate.getMonth() + 1,
          title:
            moment(currentDate)
              .format('MMMM')
              .toUpperCase() +
            ' ' +
            moment(currentDate).format('YYYY'),
          firstDate: new Date(currentDate.getTime()),
        });

        currentDate.setMonth(currentDate.getMonth() + 1);
      } else if (this.viewMode == calendarViewModes.Week) {
        //Prevent the violation of the end date range
        if (this.value.startDate < this.minDate)
          this.value.startDate = this.minDate;

        let startMonthName = moment(this.value.startDate).format('MMM');
        let endMonthName = moment(this.value.endDate).format('MMM');
        let startDay = this.value.startDate.getDate();
        let startYear = this.value.startDate.getFullYear();

        //Prevent the violation of the end date range
        if (this.value.endDate > this.maxDate)
          this.value.endDate = this.maxDate;
        if (!this.showWeekRange) {
          this.options.push({
            id: this.value.week,
            title: `${startDay} de ${startMonthName} de ${startYear}`,
            firstDate: new Date(this.value.startDate.getTime()),
          });
        } else {
          let endOfWeek = moment(this.value.startDate)
            .clone()
            .endOf('week');
          if (startMonthName === endMonthName) {
            this.options.push({
              id: this.value.week,
              title: `Semana ${
                this.value.week
              } - ${startDay} al ${endOfWeek.format(
                'DD'
              )} de ${this.$options.filters.capitalize(
                moment(this.value.startDate).format('MMMM')
              )} de ${startYear}`,
              firstDate: new Date(this.value.startDate.getTime()),
            });
          } else {
            this.options.push({
              id: this.value.week,
              title: `Semana ${
                this.value.week
              } - ${startDay} de ${this.$options.filters.capitalize(
                moment(this.value.startDate).format('MMMM')
              )} al ${endOfWeek.format(
                'DD'
              )} de ${this.$options.filters.capitalize(
                moment(this.value.endDate).format('MMMM')
              )} de ${startYear}`,
              firstDate: new Date(this.value.startDate.getTime()),
            });
          }
        }
      } else {
        let startMonthName = this.$options.filters.capitalize(
          moment(this.value.startDate).format('MMMM')
        );
        let startDay = this.value.startDate.getDate();
        let startYear = this.value.startDate.getFullYear();

        const today = new Date();
        const isToday =
          this.value.startDate.getDate() === today.getDate() &&
          this.value.startDate.getMonth() === today.getMonth() &&
          this.value.startDate.getFullYear() === today.getFullYear();

        this.options.push({
          id: this.value.day,
          title: `${
            isToday ? 'Hoy, ' : ''
          } ${startDay} de ${startMonthName} de ${startYear}`,
          firstDate: new Date(this.value.startDate.getTime()),
        });
      }
    },
    optionSelected(item) {
      let currentMonth = 0;
      let currentWeek = 0;
      let currentDay = 0;

      if (this.viewMode == calendarViewModes.Week) {
        (currentWeek = item.id), (currentMonth = item.firstDate.getMonth() + 1);
        currentDay = item.firstDate.getDate();

        this.value.startDate = item.firstDate;
        this.value.endDate = moment(item.firstDate)
          .add(1, 'week')
          .add(-1, 'day')
          .toDate();
      } else {
        currentWeek = moment(item.firstDate).week();
        currentMonth = item.firstDate.getMonth() + 1;
        currentDay = item.id;

        this.value.startDate = moment(item.firstDate)
          .startOf('day')
          .toDate();
        this.value.endDate = moment(item.firstDate)
          .endOf('day')
          .toDate();
      }

      this.value.month = currentMonth;
      this.value.week = currentWeek;
      this.value.day = currentDay;

      this.$emit('on-datarange-changed', this.value);

      this.updateOptions();
    },
    openDatePicker() {
      this.isOpen = !this.isOpen;
    },
    closeDatePicker() {
      this.isOpen = false;
    },
    setDatePickerValue(newValue) {
      if (this.viewMode == calendarViewModes.Week) {
        this.value.startDate = moment(newValue)
          .startOf('week')
          .toDate();
        this.value.endDate = moment(newValue)
          .startOf('week')
          .add(6, 'day')
          .toDate();
      } else {
        this.value.startDate = moment(newValue).toDate();
        this.value.endDate = this.value.startDate;
      }
      this.refreshSelection();
      this.closeDatePicker();
    },

    disabledAfterMaxDate(date) {
      return date > this.maxDate._d;
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/shared/scss/config/_colors.scss';

.calendar-datarange-selector {
  margin-top: 2px;

  .multi-data {
    float: left;
    line-height: 16px;
    text-align: center;
    padding: 5px 0px;
    cursor: pointer;

    &.active {
      border: 2px solid map-get($colors, 'orange');
      border-radius: 8px;
    }

    &:hover {
      border-radius: 8px;
      background: map-get($colors, 'orange');
      color: map-get($colors, 'white');
    }
  }

  .single-data {
    font: Bold 14px/17px Lato;
    padding: 0px 0px;
  }

  .single-data > li {
    list-style-type: none !important;

    &.action {
      width: 33px;
      height: 34px;
    }
  }

  .single-data .title {
    height: 34px;
    text-align: center;
    padding-top: 8px;
    overflow: hidden;
  }

  .single-data > li > a {
    min-width: 33px;
    position: relative;
    float: left;
    text-align: center;
    line-height: 34px;

    letter-spacing: 0;
    color: #11291b;
    text-decoration: none;
  }
  .single-data > ul > li {
    background: #f2f2f2 0% 0% no-repeat padding-box;
    border: 1px solid #e2e2e2;
    border-radius: 4px;
  }

  .rounded {
    border-radius: 4px;
  }
}

.subtitle {
  font-weight: normal;
  font-size: 12px;
}

.date-range {
  display: inline;
}

.date-range-icon {
  color: #2db782;
  display: inline;
  margin: 2px;
  margin-left: 6px;
  cursor: pointer;
}

.date-picker {
  position: absolute;
  z-index: 100;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  margin-top: 40px;
}

.datepicker-container {
  display: contents;
}

.date-picker div {
  display: contents;
}
</style>
